import { useState, useContext } from "react";
import Swal from "sweetalert2";
import {
    Box,
    CircularProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";

import AddCardIcon from '@mui/icons-material/AddCard';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";

import CustomButton from "../components/custom_components/CustomButton";
import CustomSearch from "../components/custom_components/CustomSearch";
import CustomIconButton from "../components/custom_components/CustomIconButton";

import AddPaymentMethod from "../components/manage_payment_methods/AddPaymentMethod";
import EditPaymentMethod from "../components/manage_payment_methods/EditPaymentMethod";

import { UserContext } from "../context/UserContext";

const apiUrl = process.env.REACT_APP_API_URL;

// ─── Type chip colours ─────────────────────────────────────────────────────────
const METHOD_CFG = {
    GCash: { bg: "#D1FAE5", fg: "#065F46", dot: "#065F46" },
    BDO: { bg: "#DBEAFE", fg: "#1E40AF", dot: "#1E40AF" },
    Maya: { bg: "#F3E5F5", fg: "#4A148C", dot: "#4A148C" },
    Cash: { bg: "#FEF9C3", fg: "#713F12", dot: "#713F12" },
};

function TypeChip({ label }) {
    const c = METHOD_CFG[label] ?? { bg: "#F3F4F6", fg: "#374151", dot: "#6B7280" };

    return (
        <Typography
            style={{
                display: "inline-flex",
                padding: "2px 8px",
                borderRadius: 3,
                fontSize: 11,
                fontWeight: 700,
                backgroundColor: c.bg,
                color: c.fg,
                whiteSpace: "nowrap",
            }}
        >
            {label}
        </Typography>
    );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function ManagePaymentMethod() {

    const { paymentMethods, setPaymentMethods } = useContext(UserContext);

    const [loading, setLoading] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const [search, setSearch] = useState("");
    const [editing, setEditing] = useState(null);

    const [openAdd, setOpenAdd] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);

    // ── delete ─────────────────────────────────────────────────────────────
    const handleDelete = async (id) => {

        const res = await Swal.fire({
            title: "Delete Payment Method?",
            text: "This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#e94d4d",
            confirmButtonText: "Delete",
        });

        if (!res.isConfirmed) return;

        setDeletingId(id);

        try {
            const response = await fetch(`${apiUrl}manage_payment_method/delete_paymethod.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    id,
                }),
            });

            const result = await response.json();

            if (result.status === "success") {
                setPaymentMethods((prev) => prev.filter((p) => p.id !== id));
            } else {
                Swal.fire({
                    text: result.message,
                    icon: "error",
                });
            }

        } catch (e) {
            console.error(e);
        } finally {
            setDeletingId(null);
        }
    };

    const filtered = paymentMethods.filter(
        (p) =>
            p.AccName.toLowerCase().includes(search.toLowerCase()) ||
            p.AccNumber.toLowerCase().includes(search.toLowerCase()) ||
            p.AccType.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <Box>

            {/* ── Page header ── */}
            <Box mb={2}>
                <Typography fontWeight={700} fontSize={16} color="#111" lineHeight={1.2}>
                    Payment Methods
                </Typography>
                <Typography fontSize={12} color="#94A3B8">
                    {paymentMethods.length} method{paymentMethods.length !== 1 ? "s" : ""} configured
                </Typography>
            </Box>

            <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={2} mb={2}
            >

                <CustomSearch
                    md="45ch"
                    placeholder="Search by Acc Name, Acc Number, or Type"
                    value={search}
                    onChange={(val) => setSearch(val)}
                />

                <CustomButton
                    label="Add"
                    startIcon={<AddCardIcon />}
                    onClick={() => setOpenAdd(true)}
                />

            </Stack>

            {/* ── Table ── */}
            <Box
                sx={{
                    border: "1px solid #E2E8F0",
                    borderRadius: 1.2,
                    overflow: "hidden",
                }}
            >
                <TableContainer sx={{ maxHeight: "100vh" }}>
                    <Table stickyHeader size="small">

                        <TableHead>
                            <TableRow>
                                {["#", "Account Name", "Account Number", "Type", "Actions"].map((h, i) => (
                                    <TableCell key={h} align={h === "Actions" ? "center" : "left"} sx={{
                                        bgcolor: "#1C2536",
                                        color: i === 0 ? "#64748B" : "#94A3B8",
                                        fontWeight: 700,
                                        fontSize: 11,
                                        letterSpacing: "0.6px",
                                        textTransform: "uppercase",
                                        borderBottom: "none",
                                        py: 1,
                                        width: h === "#" ? 48 : h === "Actions" ? 110 : "auto",
                                    }}>
                                        {h}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>

                            {/* Loading */}
                            {loading ? (

                                <TableRow>
                                    <TableCell colSpan={5}>
                                        <Stack alignItems="center" justifyContent="center" spacing={1.5} sx={{ height: 260 }}>
                                            <CircularProgress size={36} thickness={4} sx={{ color: "#111" }} />
                                            <Typography fontSize={13} color="#94A3B8" fontWeight={500}>
                                                Loading payment methods…
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                </TableRow>

                            ) : filtered.length === 0 ? (

                                <TableRow>
                                    <TableCell colSpan={5}>
                                        <Stack alignItems="center" justifyContent="center" spacing={1.5} sx={{ height: 260 }}>
                                            <Box sx={{
                                                width: 56, height: 56, borderRadius: "14px",
                                                bgcolor: "#F1F5F9",
                                                display: "flex", alignItems: "center", justifyContent: "center",
                                            }}>
                                                <PaymentsOutlinedIcon sx={{ fontSize: 26, color: "#CBD5E1" }} />
                                            </Box>
                                            <Box textAlign="center">
                                                <Typography fontWeight={600} color="#475569" fontSize={14}>
                                                    No payment methods found
                                                </Typography>
                                                <Typography fontSize={12} color="#94A3B8" mt={0.5}>
                                                    {search ? "Try a different search term" : "Click 'Add Method' to get started"}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </TableCell>
                                </TableRow>

                            ) : (
                                filtered.map((pm, index) => (
                                    <TableRow
                                        key={pm.id}
                                        sx={{
                                            cursor: "pointer",
                                            "&:last-child td": { borderBottom: "none" },
                                            "&:hover": { bgcolor: "#dcfbff" },
                                            transition: "background .1s",
                                        }}
                                    >

                                        {/* # */}
                                        <TableCell>
                                            <Typography fontSize={11} color="#CBD5E1" fontWeight={600}>
                                                {String(index + 1).padStart(2, "0")}
                                            </Typography>
                                        </TableCell>

                                        {/* Account Name */}
                                        <TableCell>
                                            <Typography fontWeight={600} fontSize={12} color="#0F172A">
                                                {pm.AccName}
                                            </Typography>
                                        </TableCell>

                                        {/* Account Number */}
                                        <TableCell>
                                            <Typography
                                                fontSize={12}
                                                fontWeight={600}
                                                color="#64748B"
                                            >
                                                {pm.AccNumber}
                                            </Typography>
                                        </TableCell>

                                        {/* Type */}
                                        <TableCell>
                                            <TypeChip label={pm.AccType} />
                                        </TableCell>

                                        {/* Actions */}
                                        <TableCell align="center">
                                            <Stack direction="row" spacing={1} justifyContent="center">

                                                <CustomIconButton
                                                    type="success"
                                                    icon={<EditIcon fontSize="small" />}
                                                    onClick={() => {
                                                        setEditing(pm);
                                                        setOpenEdit(true);
                                                    }}
                                                />

                                                <CustomIconButton
                                                    type="delete"
                                                    loading={deletingId === pm.id}
                                                    disabled={deletingId === pm.id}
                                                    icon={<DeleteIcon fontSize="small" />}
                                                    onClick={() => handleDelete(pm.id)}
                                                />

                                            </Stack>
                                        </TableCell>

                                    </TableRow>
                                ))
                            )}

                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Footer */}
                {!loading && filtered.length > 0 && (
                    <Box sx={{
                        px: 2, py: 1.25,
                        borderTop: "1px solid #F1F5F9",
                        bgcolor: "#FAFAFA",
                    }}>
                        <Typography fontSize={11} color="#94A3B8">
                            Showing <strong>{filtered.length}</strong> of <strong>{paymentMethods.length}</strong> payment method{paymentMethods.length !== 1 ? "s" : ""}
                        </Typography>
                    </Box>
                )}
            </Box>

            <AddPaymentMethod
                open={openAdd}
                onClose={() => setOpenAdd(false)}
                onUpdate={(val) =>
                    setPaymentMethods(prev => [val, ...prev])
                }
            />

            <EditPaymentMethod
                open={openEdit}
                onClose={() => setOpenEdit(false)}
                onUpdate={(val) =>
                    setPaymentMethods(prev =>
                        prev.map(pmt =>
                            pmt.id === val.id
                                ? {
                                    ...pmt,
                                    AccName: val.AccName,
                                    AccNumber: val.AccNumber,
                                    AccType: val.AccType,
                                }
                                : pmt
                        )
                    )
                }
                pm={editing}
            />

        </Box>
    );
}

